/**
 * wantsWrite: the want pop-up's Save and the profile's ✕ write to the server.
 *
 * Card 1218637972579066 item 4 (PR B), Blake's ruling of 2026-09-27: the wizard's Trade For step
 * is item-only, and its pop-up puts one entry on THIS item and, on a switch, also on the seller's
 * Looking For. The switch has to work, and the web never posted a want: app.js's `saveWantDraft`
 * pushed into the page's `myLookingFor` and toasted "we'll flag matches", and `[data-lfremove]`
 * spliced the same list, so a want added or removed on the web vanished on reload and never
 * reached the matcher (card 1218431365264102). The write guard now suppresses those two
 * fabricators on the installed site (`#want-save`, `[data-lfremove]` in GUARDED_WRITES), and this
 * module does the real thing through the bridge:
 *
 *   Save, from the wizard, switch off:  the entry goes on the item (no request), "Added to this item".
 *   Save, from the wizard, switch on:   the entry goes on the item FIRST (from the draft at the
 *                                       click), then POST /api/wants; on 201 the page's list is
 *                                       rebuilt from the server (installWants), or the created row
 *                                       is appended when that read fails; on a refusal the toast
 *                                       says the profile half did not happen, and why; when the
 *                                       server never answered, the list is re-read first and the
 *                                       toast says saved, not saved, or could not confirm.
 *   Save, from the profile:             the button reads "Saving…"; POST /api/wants; on 201 the
 *                                       pop-up closes and the toast speaks, then the list is
 *                                       rebuilt from the server and the toast is shown again
 *                                       (the repaint writes a fresh toast node); a re-read
 *                                       that hangs holds the pop-up no longer than the 201
 *                                       does; on a refusal the pop-up stays open with the
 *                                       reason, or a toast says it if the pop-up was closed
 *                                       meanwhile, and nothing is pushed.
 *   ✕ on a want the server holds:       DELETE /api/wants/:id; on 200 the row leaves the page by
 *                                       id at once, then the list is re-read; a want the page
 *                                       alone holds (the tour's fixtures) is dropped locally.
 *
 * The pop-up's own checks (a category; a subcategory where the category has them; a name, which the
 * server requires; no comma in the item's name; a grade that is a number from 0 to 10; whole-number
 * sizes) run in app.js (`wantDraftError`) and are read through the bridge, so the standalone demo
 * and the installed site refuse the same drafts with the same words.
 */
import { installWants, mapWants, type ApiWant } from "./floorTransport";

export type AuthFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** app.js's `wantModal.draft`, as `freshWantDraft` shapes it. */
export interface WantDraft {
  name: string;
  cat: string | null;
  sub: string | null;
  cond: string;
  graded: boolean;
  minGrade: string;
  wrist: string;
  shirt: string | null;
  waist: string;
  inseam: string;
  shoe: string;
  sealed: boolean;
  notes: string;
  addToProfile?: boolean;
}

/** data.js's `wantFieldsFor(sub || cat)`: which of the pop-up's dependent fields apply. */
export interface WantFields {
  graded?: boolean;
  wrist?: boolean;
  shirt?: boolean;
  pants?: boolean;
  shoe?: boolean;
  sealed?: boolean;
  noCondition?: boolean;
}

interface WantDraftWire { from: string; addToProfile: boolean; draft: WantDraft; fields: WantFields; token: number }

interface WantWindow extends Window {
  __gbzWantDraft?: () => WantDraftWire | null;
  __gbzWantDraftError?: () => string | null;
  /** false when the pop-up the request came from (by token) is no longer the open one: the caller
   *  toasts instead. Clears the pop-up's Saving state. */
  __gbzWantShowError?: (message: string, token?: number) => boolean;
  /** The draft's duplicate key: the fields the pop-up would send, normalized (the bridge's wantKey). */
  __gbzWantKey?: () => string | null;
  /** Whether the entry is still on the wizard's item and picked when the server answers. */
  __gbzWantOnItem?: (label: string) => boolean;
  /** Whether a row with that duplicate key is on the page's list (after a re-read: was it saved?). */
  __gbzWantOnList?: (key: string) => boolean;
  /** Drops the page row with that server id; false when no row has it. */
  __gbzWantRemoveById?: (id: string) => boolean;
  /** The pop-up's Saving state (button disabled, "Saving…"), for the pop-up with that token. */
  __gbzWantBusy?: (busy: boolean, token?: number) => void;
  /** null when there is no listing to add the entry to (the wizard was left under the pop-up); with a
   *  token, closes the pop-up only if it is still the one the request came from. */
  __gbzWantCommit?: (opts: { item: boolean; close: boolean; token?: number }) => string | null;
  __gbzWantAt?: (index: number) => { id?: string } | null;
  __gbzWantRemoveLocal?: (index: number) => void;
  __gbzWantDuplicate?: () => boolean;
  __gbzWantAppend?: (want: unknown) => number;
  rerenderKeepScroll?: () => void;
  toast?: (message: string, ms?: number) => void;
  /** The module's numbered list re-read, for the boot-time read (WebInstall.tsx): an answer that lands
   *  after a newer read was started is dropped, so a boot read cannot overwrite a save's. Throws when
   *  the read failed outright, so the boot caller's catch logs it. */
  __gbzWantsReread?: () => Promise<number>;
}

export const SAVE_SELECTOR = "#want-save";
export const REMOVE_SELECTOR = "[data-lfremove]";

export const ADDED_TO_ITEM = "Added to this item";
export const ADDED_TO_BOTH = "Added to this item and your Looking For";
/* The wizard's toasts when the profile half did not happen: the server refused the want (400), the
   session had expired (401), the server failed or never answered (and the re-read did not show the
   want), or it was already there. */
export const ADDED_TO_ITEM_REFUSED = "Added to this item. Your Looking For didn't take it: check the sizes and grade, then add it from your profile.";
export const ADDED_TO_ITEM_UNREACHED = "Added to this item. Your Looking For didn't save it. Try again from your profile.";
export const ADDED_TO_ITEM_SIGNIN = "Added to this item. Sign in again to add it to your Looking For.";
export const ADDED_TO_ITEM_ALREADY = "Added to this item. It's already on your Looking For.";
/* The wizard was left under the pop-up (Back mid-edit), so there was no item to add it to. Not
   "closed": the site says a listing is closed when it has ended. */
export const LISTING_CLOSED = "The listing was left before this could be added to it. Nothing was added.";
/** A saved want, from the profile or from the wizard when the item half is gone. The demo's old
 *  "we'll flag matches the moment they appear" promised what a flag, a rate guard and a per-want
 *  cap decide; the fact is enough. */
export const WANT_SAVED = "Added to your Looking For";
export const ADDED_TO_PROFILE_ONLY = WANT_SAVED;
export const WANT_REFUSED = "Couldn't save that want. Check the category, sizes, and grade, then try again.";
export const WANT_FAILED = "Couldn't save that want. Try again.";
export const WANT_SIGNIN = "Sign in again to save that want.";
export const WANT_DUPLICATE = "That's already on your Looking For.";
/* The same want's first request is still out: not "already there", which it may yet fail to be. */
export const WANT_PENDING = "That want is still being saved. Give it a moment.";
export const ADDED_TO_ITEM_PENDING = "Added to this item. Your Looking For is still saving it.";
export const WANT_NOT_REMOVED = "Couldn't remove that want. Try again.";
/* The server never answered the save AND the list could not be re-read: the outcome is unknown, so
   the words do not claim a failure and do not invite a blind retry. */
export const WANT_UNCONFIRMED = "Couldn't confirm that save. Check your Looking For before adding it again.";
export const ADDED_TO_ITEM_UNCONFIRMED = "Added to this item. Couldn't confirm your Looking For took it; check there before adding it again.";
/** A toast that says what did not happen stays up long enough to be read (the demo's default is 2.6 s). */
export const LONG_TOAST_MS = 6000;
/** data.js's own toast life: a toast is shown again after a repaint only within it. */
export const TOAST_LIFE_MS = 2600;

/* No request this module makes, nor the list re-read after a write, can hang forever: each carries
   this signal (absent where the runtime has no AbortSignal.timeout). authFetch's own sign-in
   refresh after a 401 (AuthContext.tsx) is outside it. */
const timeoutSignal = (): AbortSignal | undefined => (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(20000) : undefined);

const whole = (v: unknown): number | undefined => {
  const t = String(v ?? "").trim();
  return /^\d+$/.test(t) ? parseInt(t, 10) : undefined;
};
const decimal = (v: unknown): number | undefined => {
  const t = String(v ?? "").trim();
  const n = Number(t);
  return t && Number.isFinite(n) ? n : undefined;
};

/** The pop-up's draft as POST /api/wants takes it: the iPhone app's field names (LookingForSection.tsx),
 *  only the fields that apply to the category, trimmed, numbers as numbers, "Any" not sent. A value
 *  that is not a number is left out rather than sent as NaN. */
export function wantPostBody(d: WantDraft, f: WantFields): Record<string, unknown> {
  const body: Record<string, unknown> = { category: d.cat, subcategory: d.sub || "", title: String(d.name ?? "").trim() };
  const cond = String(d.cond ?? "");
  if (!f.noCondition && cond && cond !== "Any") body.condition = cond;
  const notes = String(d.notes ?? "").trim();
  if (notes) body.notes = notes;
  /* sizeUs is NOT sent: the matcher compares it to a listing's "US 10.5" as exact text
     (listingMatcher.ts:121-124), so a sized sneaker want could never match. The app has the field
     but never sends it in practice: its sneaker ids ('sneakers', 'shoes', LookingForSection.tsx)
     never match the real slug 'sneakers-shoes'. Card 1218915410665860 (server). The shoe input is
     hidden with it. */
  if (f.shirt) {
    if (d.shirt) body.shirtSize = d.shirt;
    const waist = whole(d.waist); if (waist !== undefined) body.pantWaist = waist;
    const inseam = whole(d.inseam); if (inseam !== undefined) body.pantInseam = inseam;
  }
  /* wristMm is NOT sent: routes/wants.ts:25 takes 30 to 60, a wrist is 140 to 220 mm, and the
     field's own placeholder says "e.g. 180"; the app sends the seller's number and gets the same
     400 (LookingForSection.tsx:458). Card 1218917326958161 (server). One line brings it back. */
  if (f.sealed && d.sealed) body.newSealedOnly = true;
  if (f.graded && d.graded) {
    body.graded = true;
    const grade = decimal(d.minGrade); if (grade !== undefined) body.minGrade = grade;
  }
  return body;
}

export function installWantsWrite(
  win: Window,
  authFetch: AuthFetch,
  {
    notify = (message: string, ms?: number) => {
      if (!win.document.getElementById("toast")) return;
      (win as WantWindow).toast?.(message, ms);
    },
    refresh = (fetchImpl: AuthFetch) => installWants(win, fetchImpl),
  }: { notify?: (m: string, ms?: number) => void; refresh?: (fetchImpl: AuthFetch) => Promise<number> } = {},
): () => void {
  const target = win as WantWindow;
  /* The deletes in flight, by want id: a second ✕ on the same want is dropped, a ✕ on another want
     goes out. */
  const removing = new Set<string>();
  /* The profile's saves in flight, by pop-up: a double-click on one Save is one request, while a
     Save from a pop-up opened after the last one closed goes out on its own instead of being
     dropped without a word. */
  const inflight = new Set<number>();
  /* The wants posted and not yet on the page, by duplicate key: the same want saved again before
     the server answers and the list is re-read is not posted twice; the pop-up says it is still
     being saved. */
  const pending = new Set<string>();
  /* The list re-reads, numbered: only the newest is applied. A read that lands after a newer one
     was started is thrown away before it reaches the page (checked when the headers arrive and
     again once the body has). appliedSeq is the newest read that did reach the page: a save whose
     own re-read failed appends its row only if no read started at or after it has landed since,
     because one that did carries the server's truth (with or without the row). */
  let readSeq = 0;
  let appliedSeq = 0;

  /* One request, its status, and the row the server answered with (a created want on 201). Status
     0: no answer at all (the network, the 20-second timeout), so the server may still have it. */
  async function post(body: Record<string, unknown>): Promise<{ ok: boolean; status: number; row: unknown }> {
    try {
      const res = await authFetch("/api/wants", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body), signal: timeoutSignal() });
      let row: unknown = null;
      try { row = await res.json(); } catch { row = null; }
      return { ok: res.ok, status: res.status, row };
    } catch {
      return { ok: false, status: 0, row: null };
    }
  }

  /* The list from the server, through a fetch that carries the timeout and drops a superseded
     answer. n is what installWants installed (0 on a failed or dropped read, the list untouched;
     0 too on a successful read of an empty list, which replaces it); mine is this read's number. */
  type ReadResult = { n: number; mine: number; landed: boolean; dropped: boolean };
  /* The newest read's promise: a read dropped for a newer one follows that one (settled) instead of
     reading again, so a good answer already on its way, the boot read's for one, is not thrown away. */
  let newest: Promise<ReadResult> | null = null;
  function reread(): Promise<ReadResult> {
    const mine = ++readSeq;
    const run = (async (): Promise<ReadResult> => {
      let landed = false, dropped = false, fetched = false;
      const gated: AuthFetch = async (input, init) => {
        fetched = true;
        const res = await authFetch(input, { ...init, signal: timeoutSignal() });
        /* The body is read here, under the same check, so a newer read cannot start between the
           headers and the body and then be applied before this one. */
        if (mine !== readSeq) { dropped = true; throw new Error("superseded by a newer read (headers)"); }
        const text = await res.text();
        if (mine !== readSeq) { dropped = true; throw new Error("superseded by a newer read (body)"); }
        landed = res.ok;
        return new Response(text, { status: res.status, statusText: res.statusText, headers: res.headers });
      };
      let n = 0;
      /* A reader that resolved with rows without going through this fetch (a test's stub) is trusted
         as landed; one that answered 0 that way is read as a failed read, the way installWants's own 0
         on a refused GET is. */
      try { n = await refresh(gated); if (!fetched && n > 0) landed = true; } catch { n = 0; landed = false; }
      if (landed) appliedSeq = Math.max(appliedSeq, mine);
      return { n, mine, landed, dropped };
    })();
    newest = run;
    return run;
  }
  /* A read dropped for a newer one: the newer one's answer is this read's answer (and so on, while
     that one was dropped too). Ends on a read that landed or failed. */
  async function settled(r: ReadResult): Promise<ReadResult> {
    while (r.dropped && newest) {
      const next = await newest;
      if (next.mine === r.mine) break;
      r = next;
    }
    return r;
  }

  /* After a saved want: the list from the server. If nothing has ever reached the page (this read
     failed or was dropped, and no earlier read landed either, as when the boot read was the one
     dropped), follow the newest read if one is newer (landed or failed, so a failed one is not read
     again), or read once more if none is, before settling for less. Then, if no read started at
     or after THIS save's own read has reached the page (a newer
     read is still out, or every read failed), the created row itself, so the page shows it and a
     second Save does not make a second want. A later read that landed (the boot's, a ✕'s) carries
     the server's truth, with or without the row, whatever the read this one followed did. */
  async function rebuildOrAppend(row: unknown): Promise<void> {
    const first = await reread();
    let r = await settled(first);
    if (appliedSeq === 0 && !r.landed) r = await settled(await (r.mine < readSeq && newest ? newest : reread()));
    if (appliedSeq >= first.mine) return;
    if (r.n === 0 && row && typeof row === "object") {
      const mapped = mapWants([row as ApiWant]);
      if (mapped[0]) target.__gbzWantAppend?.(mapped[0]);
    }
  }

  /* A repaint writes a fresh, empty toast node (app.js's render; wizardPublish.ts names the same
     trap), and the re-read repaints the profile: a toast shown before it is shown again after it,
     when the repaint emptied it, within the toast's own life, and with the words as they are at
     that moment (the chip may have been tapped off meanwhile). A node holding other words (a newer
     toast) is left alone. */
  function retoast(words: () => string, shownAt: number): void {
    if (Date.now() - shownAt > TOAST_LIFE_MS) return;
    const t = win.document.getElementById("toast");
    if (!t || !t.textContent) notify(words());
  }

  /* A POST with no answer (the network, the timeout, a proxy's 502/504): the server may have taken
     it. The list is re-read first: on it now, it was saved and no retry is invited; not on a list
     that landed, it was not saved; and when the re-read did not land either, the outcome is
     unknown and the words say so. */
  const noAnswer = (status: number): boolean => status === 0 || status === 502 || status === 504;
  async function savedAfterAll(key: string | null): Promise<{ saved: boolean; known: boolean }> {
    const r = await settled(await reread());
    const saved = key !== null && target.__gbzWantOnList?.(key) === true;
    return { saved, known: saved || r.landed };
  }

  async function save(): Promise<void> {
    const w = target.__gbzWantDraft?.();
    if (!w) return;
    const err = target.__gbzWantDraftError?.();
    if (err) { target.__gbzWantShowError?.(err, w.token); return; }
    const forItem = w.from === "wizard";
    /* The body, the key and the duplicate checks are taken at the click, before any await, so what
       the server gets is what the seller pressed Save on. */
    const body = wantPostBody(w.draft, w.fields);
    const key = target.__gbzWantKey?.() ?? null;
    const onList = !!target.__gbzWantDuplicate?.();
    const stillSaving = key !== null && pending.has(key);
    if (forItem) {
      /* The item's half is the page's own and cannot fail; it goes first, so a ✕ or an edit during
         the request cannot lose it or change it. null: the wizard was left under the pop-up (Back
         mid-edit), so there is no listing to add it to; the pop-up closes and nothing is claimed. */
      const label = target.__gbzWantCommit?.({ item: true, close: true, token: w.token }) ?? null;
      if (!w.addToProfile) { notify(label ? ADDED_TO_ITEM : LISTING_CLOSED, label ? undefined : LONG_TOAST_MS); return; }
      if (onList) { notify(label ? ADDED_TO_ITEM_ALREADY : WANT_DUPLICATE, LONG_TOAST_MS); return; }
      if (stillSaving) { notify(label ? ADDED_TO_ITEM_PENDING : WANT_PENDING, LONG_TOAST_MS); return; }
      /* No latch by pop-up here: it closed at the commit, so each request is its own pop-up's. */
      if (key !== null) pending.add(key);
      /* The entry may have left the item while a request was out (the chip tapped off, the wizard
         left): read at each toast, so the toast says only what is still true. */
      const stillOnItem = (): boolean => !!label && target.__gbzWantOnItem?.(label) !== false;
      try {
        const res = await post(body);
        if (res.ok) {
          const words = stillOnItem() ? ADDED_TO_BOTH : ADDED_TO_PROFILE_ONLY;
          notify(words); const shownAt = Date.now();
          await rebuildOrAppend(res.row);
          retoast(() => (stillOnItem() ? ADDED_TO_BOTH : ADDED_TO_PROFILE_ONLY), shownAt);
          return;
        }
        const after = noAnswer(res.status) ? await savedAfterAll(key) : { saved: false, known: true };
        const onItem = stillOnItem();
        if (after.saved) { notify(onItem ? ADDED_TO_BOTH : ADDED_TO_PROFILE_ONLY); return; }
        if (!after.known) { notify(onItem ? ADDED_TO_ITEM_UNCONFIRMED : WANT_UNCONFIRMED, LONG_TOAST_MS); return; }
        const refused = res.status === 400, signin = res.status === 401;
        if (onItem) notify(refused ? ADDED_TO_ITEM_REFUSED : signin ? ADDED_TO_ITEM_SIGNIN : ADDED_TO_ITEM_UNREACHED, LONG_TOAST_MS);
        else notify(refused ? WANT_REFUSED : signin ? WANT_SIGNIN : WANT_FAILED, LONG_TOAST_MS);
      } finally {
        if (key !== null) pending.delete(key);
      }
      return;
    }
    /* A second click on the same pop-up while its request is out (the button is disabled, but a
       synthetic click can still reach here): dropped, before any words. */
    if (inflight.has(w.token)) return;
    if (onList) { target.__gbzWantShowError?.(WANT_DUPLICATE, w.token); return; }
    if (stillSaving) { target.__gbzWantShowError?.(WANT_PENDING, w.token); return; }
    inflight.add(w.token);
    if (key !== null) pending.add(key);
    try {
      target.__gbzWantBusy?.(true, w.token);
      const res = await post(body);
      const after = noAnswer(res.status) ? await savedAfterAll(key) : { saved: false, known: true };
      if (res.ok || after.saved) {
        /* Closes the pop-up the request came from, never one opened since; the toast speaks on the
           server's answer (or on the re-read that showed the want), the re-read comes after, and the
           toast is shown again where the repaint took it. */
        target.__gbzWantCommit?.({ item: false, close: true, token: w.token });
        notify(WANT_SAVED); const shownAt = Date.now();
        if (res.ok) await rebuildOrAppend(res.row);
        retoast(() => WANT_SAVED, shownAt);
        return;
      }
      const words = !after.known ? WANT_UNCONFIRMED : res.status === 401 ? WANT_SIGNIN : res.status === 400 ? WANT_REFUSED : WANT_FAILED;
      /* Shown in the pop-up the request came from; if that one was closed, said in a toast instead. */
      if (target.__gbzWantShowError?.(words, w.token) !== true) notify(words, LONG_TOAST_MS);
    } finally {
      inflight.delete(w.token);
      if (key !== null) pending.delete(key);
    }
  }

  async function remove(index: number): Promise<void> {
    if (!Number.isFinite(index)) return;
    const w = target.__gbzWantAt?.(index);
    if (!w) return;
    if (!w.id) { target.__gbzWantRemoveLocal?.(index); return; }
    if (removing.has(w.id)) return;
    removing.add(w.id);
    try {
      let ok = false;
      try {
        const res = await authFetch(`/api/wants/${encodeURIComponent(w.id)}`, { method: "DELETE", signal: timeoutSignal() });
        ok = res.ok;
      } catch { ok = false; }
      if (!ok) { notify(WANT_NOT_REMOVED, LONG_TOAST_MS); return; }
      /* The server no longer has it: the row leaves the page at once, by id (a ✕ on another want
         meanwhile may have moved the index), then the list is re-read to agree with the server. */
      target.__gbzWantRemoveById?.(w.id);
      await reread();
    } finally {
      removing.delete(w.id);
    }
  }

  const onClick = (event: Event): void => {
    const el = event.target as Element | null;
    if (!el?.closest) return;
    /* No stopPropagation: the guard suppresses app.js's own handler, and letting it run is the
       fail-safe (the same reasoning as wizardPublish.ts's publish click). */
    if (el.closest(SAVE_SELECTOR)) { void save(); return; }
    const chip = el.closest<HTMLElement>(REMOVE_SELECTOR);
    if (chip) void remove(parseInt(chip.dataset.lfremove ?? "", 10));
  };

  /* The boot-time read goes through the same numbering (WebInstall.tsx), so a boot answer that lands
     after a save's re-read cannot overwrite it. */
  target.__gbzWantsReread = async () => {
    const r = await settled(await reread());
    if (!r.landed && !r.dropped) throw new Error("wants: the list could not be read");
    return r.n;
  };
  win.document.addEventListener("click", onClick, true);
  return () => {
    win.document.removeEventListener("click", onClick, true);
    delete target.__gbzWantsReread;
  };
}
